import { ApolloProvider } from '@apollo/client'
import { StrictMode } from 'react'
import { CookiesProvider } from 'react-cookie'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router'
import './i18n'



import { AboutPage } from '@pages/about'
import { BlogDetailPage, BlogPage } from '@pages/blog'
import { CartPage } from '@pages/cart'
import { CategoryChildrenCatalogPage, NewProductsPage, PopularProductsPage, PreviouslyOrderedProductsPage, ProductGroupPage, SaleProductsPage } from '@pages/catalog'
import { ContactsPage } from '@pages/contacts'
import { CustomerInfoPage } from '@pages/customer-info'
import { LandingPage } from '@pages/landing'
import { MainPage } from '@pages/main'
import { NoMatchPage } from '@pages/no-match'
import { OrderPage } from '@pages/order'
import { ProfilePage } from '@pages/personal/profile'
import { ProductDetailPage } from '@pages/product'
import { SearchPage } from '@pages/search'
import { TestErrorPage } from '@pages/test-error'

import { personalRoutes } from '@widgets/personal/nav'


import { client } from '@shared/api/client'
import {
  URL_BLOG,
  URL_BLOG_CATEGORY,
  URL_BLOG_POST,
  URL_BLOG_TAG,
  URL_BLOG_THEME,
  URL_CART,
  URL_CATEGORY_NEW_PRODUCTS,
  URL_CATEGORY_POPULAR_PRODUCTS,
  URL_CATEGORY_PREVIOUSLY_ORDERED,
  URL_CATEGORY_SALE_PRODUCTS,
  URL_CHECKOUT,
  URL_CUSTOMER_PROFILE,
  URL_LANDING,
  URL_LEGACY_CATEGORY,
  URL_LEGACY_CATEGORY_CHILDREN,
  URL_LEGACY_CATEGORY_SLUG,
  URL_LEGACY_CATEGORY_SUB_CHILDREN,
  URL_MAIN_PAGE,
  URL_PAGE_ABOUT,
  URL_PAGE_CATALOG,
  URL_PAGE_CATALOG_1ST_LVL,
  URL_PAGE_CONTACTS,
  URL_PAGE_CUSTOMER_INFO,
  URL_PAGE_PRODUCT,
  URL_PAGE_PRODUCT_GROUP,
  URL_PAGE_TEST_SENTRY_ERROR,
  URL_SEARCH
} from '@shared/contants'
import analytics, { PageViewTracker } from '@shared/lib/analytics'
import { EmitterProvider } from '@shared/lib/emitter'
import { loadJivoSiteScript } from '@shared/lib/jivosite'
import { LocaleLayout, LocaleProvider, LocaleRedirect } from '@shared/lib/locale'
import { initSentry } from '@shared/lib/sentry'
import { bootstrapTheme } from '@shared/lib/theme/bootstrap'
import { CatalogRedirect } from '@shared/ui/catalog-redirect'
import { CategoryRedirect } from '@shared/ui/category-redirect'
import { SentryErrorBoundary } from '@shared/ui/sentry-error-boundary'
import { RouteSEO } from '@shared/ui/seo/RouteSEO'


import '@shared/styles/app.scss'

import RootLayout from './layouts'
import PersonalLayout from './layouts/personal'


initSentry()

// Инициализируем JivoSite
loadJivoSiteScript()


// Инициализируем аналитику (GA4 + Яндекс.Метрика)
analytics.init()

// Ранний bootstrap темы (синхронно применит сохраненные значения, затем обновит с бэка)
// Не ждем завершения, чтобы не блокировать рендер; сохраненные значения применяются мгновенно
void bootstrapTheme()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary>
      <ApolloProvider client={client}>
        <CookiesProvider defaultSetOptions={{ path: URL_MAIN_PAGE }}>
          <EmitterProvider>
            <BrowserRouter>
              <LocaleProvider>
                <RouteSEO />
                <PageViewTracker />
                <Routes>
                  <Route path={URL_MAIN_PAGE} element={<RootLayout />}>
                    <Route index element={<MainPage />} />
                  </Route>

                  <Route path="/:locale" element={<LocaleLayout />}>
                    <Route element={<RootLayout />}>
                      <Route index element={<MainPage />} />

                      {/* Legacy /category* --> /catalog */}
                      <Route path={URL_LEGACY_CATEGORY.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_SLUG.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_CHILDREN.slice(1)} element={<CategoryRedirect />} />
                      <Route path={URL_LEGACY_CATEGORY_SUB_CHILDREN.slice(1)} element={<CategoryRedirect />} />

                      {/** Catalog pages */}
                      {/**
                        * NOTE
                        * Root catalog page redirects to main.
                        * All category levels use single route with slug - parent determined from backend.
                        */}
                      <Route path={URL_PAGE_CATALOG.slice(1)} element={<CatalogRedirect />} />
                      <Route
                        path={URL_PAGE_CATALOG_1ST_LVL.slice(1)}
                        element={<CategoryChildrenCatalogPage />}
                      />

                      <Route path={URL_CATEGORY_SALE_PRODUCTS.slice(1)} element={<SaleProductsPage />} />
                      <Route path={URL_CATEGORY_POPULAR_PRODUCTS.slice(1)} element={<PopularProductsPage />} />
                      <Route path={URL_CATEGORY_PREVIOUSLY_ORDERED.slice(1)} element={<PreviouslyOrderedProductsPage />} />
                      <Route path={URL_CATEGORY_NEW_PRODUCTS.slice(1)} element={<NewProductsPage />} />

                      <Route
                        path={URL_PAGE_PRODUCT_GROUP.slice(1)}
                        element={<ProductGroupPage />}
                      />



                      {/** Product pages */}
                      <Route path={URL_PAGE_PRODUCT.slice(1)} element={<ProductDetailPage />} />


                      {/** Backend-configured landing pages (promo / event / content) */}
                      <Route path={URL_LANDING.slice(1)} element={<LandingPage />} />


                      {/** Cart pages */}
                      <Route path={URL_CART.slice(1)} element={<CartPage />} />


                      {/** Profile pages */}
                      <Route path={URL_CUSTOMER_PROFILE.slice(1)} element={<PersonalLayout />}>
                        <Route index element={<ProfilePage />} />
                        {personalRoutes.map(({ path, element }) => (
                          <Route key={path} path={path} element={element} />
                        ))}
                      </Route>

                      {/** Checkout pages */}
                      <Route path={URL_CHECKOUT.slice(1)} element={<OrderPage />} />


                      {/** Search pages */}
                      <Route path={URL_SEARCH.slice(1)} element={<SearchPage />} />


                      {/** Static pages */}
                      <Route path={URL_PAGE_ABOUT.slice(1)} element={<AboutPage />} />
                      <Route path={URL_PAGE_CONTACTS.slice(1)} element={<ContactsPage />} />
                      <Route path={URL_PAGE_CUSTOMER_INFO.slice(1)} element={<CustomerInfoPage />} />
                      {/* <Route path={URL_PAGE_POLICY} element={<PolicyPage />} /> */}
                      {/* <Route
                        path={URL_PAGE_LOYALTY.slice(1)}
                        element={<LoyaltyProgramPage />}
                      /> */}


                      <Route path={URL_BLOG.slice(1)} element={<BlogPage />} />

                      <Route path={URL_BLOG_CATEGORY.slice(1)} element={<BlogPage />} />
                      <Route path={URL_BLOG_THEME.slice(1)} element={<BlogPage />} />
                      <Route path={URL_BLOG_TAG.slice(1)} element={<BlogPage />} />

                      <Route path={URL_BLOG_POST.slice(1)} element={<BlogDetailPage />} />
                      {/* временно скрыли Блог */}

                      {/* Тестовая страница для проверки Sentry */}
                      { process.env.NODE_ENV === 'development' && <Route path={URL_PAGE_TEST_SENTRY_ERROR} element={<TestErrorPage />} /> }

                      <Route path="*" element={<NoMatchPage />} />
                    </Route>
                  </Route>

                  <Route path="*" element={<LocaleRedirect />} />
                </Routes>
              </LocaleProvider>
            </BrowserRouter>
          </EmitterProvider>
        </CookiesProvider>
      </ApolloProvider>
    </SentryErrorBoundary>
  </StrictMode>,
)
